import { useMemo } from 'react';
import { Box, Typography, Grid, Button, Paper } from '@mui/material';
import ReportDataGrid from '../../../components/Common/ReportDataGrid';
import { formatWeight, formatDecimal } from '../../../components/Common/ReportDataGrid/utils';
import { BarChart } from '@mui/x-charts/BarChart';
import DownloadIcon from '@mui/icons-material/Download';
import { trakrefBlue } from '../../../color_utils';

const columns = [
    {
        field: 'displayLocation',
        headerName: 'Location',
        sortable: false,
    },
    {
        field: 'materialType',
        headerName: 'Material Type',
    },
    {
        field: 'netGasAddedLbs',
        headerName: 'Net Gas Added (lbs)',
        type: 'number',
        valueFormatter: formatWeight,
    },
    {
        field: 'gwp',
        headerName: 'GWP',
        type: 'number',
    },
    {
        field: 'emissionsCo2e',
        headerName: 'Emissions (lbs CO2e)',
        type: 'number',
        valueFormatter: formatWeight,
    },
    {
        field: 'emissionsMtCo2e',
        headerName: 'Emissions (MtCO2e)',
        type: 'number',
        valueFormatter: formatDecimal(4),
    },
];

const EmissionsByLocation = ({ rows, chartData, loading }) => {
    const chartLocations = chartData.map(d => d.location);
    const chartValues = chartData.map(d => Math.round(d.emissionsCo2e));

    const groupedRows = useMemo(() => {
        const sorted = [...rows].sort((a, b) => (a.location || '').localeCompare(b.location || ''));
        let lastLocation = null;
        return sorted.map((row) => {
            const showLocation = row.location !== lastLocation;
            lastLocation = row.location;
            return { ...row, displayLocation: showLocation ? row.location : '' };
        });
    }, [rows]);

    const handleExportCsv = () => {
        const headers = ['Location', 'Material Type', 'Net Gas Added (lbs)', 'GWP', 'Emissions (lbs CO2e)', 'Emissions (MtCO2e)'];
        const csvRows = groupedRows.map(r => [
            `"${r.displayLocation || ''}"`,
            `"${r.materialType || ''}"`,
            `${parseFloat(Number(r.netGasAddedLbs || 0).toFixed(3))} lbs.`,
            r.gwp ?? '',
            `${parseFloat(Number(r.emissionsCo2e || 0).toFixed(3))} lbs.`,
            (r.emissionsMtCo2e || 0).toFixed(4)
        ]);

        const csvContent = [headers.join(','), ...csvRows.map(r => r.join(','))].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        link.href = URL.createObjectURL(blob);
        link.download = 'emissions_by_location.csv';
        link.click();
        URL.revokeObjectURL(link.href);
    };

    return (
        <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 0.5 }}>
                Emissions by Location (Service-Based)
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Aggregated emissions by location (highest to lowest), with a bar chart visualization and export option
            </Typography>
            <Grid container spacing={2}>
                {/* Bar Chart */}
                <Grid item xs={12} md={8}>
                    {chartLocations.length > 0 ? (
                        <BarChart
                            xAxis={[{ scaleType: 'band', data: chartLocations, tickLabelStyle: { angle: -45, textAnchor: 'end', fontSize: 11 } }]}
                            series={[{ data: chartValues, label: 'Emissions (lbs CO2e)', color: trakrefBlue }]}
                            height={400}
                            margin={{ left: 80, bottom: 110, right: 20 }}
                        />
                    ) : (
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 400 }}>
                            <Typography color="text.secondary">No data available</Typography>
                        </Box>
                    )}
                </Grid>

                {/* Data Table + Export */}
                <Grid item xs={12} md={4}>
                    <Paper variant="outlined" sx={{ p: 2 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 'bold' }}>
                                Chart Data
                            </Typography>
                            <Button
                                variant="contained"
                                size="small"
                                startIcon={<DownloadIcon />}
                                onClick={handleExportCsv}
                                sx={{ backgroundColor: trakrefBlue, '&:hover': { backgroundColor: trakrefBlue } }}
                                disabled={rows.length === 0}
                            >
                                Export CSV
                            </Button>
                        </Box>
                        <ReportDataGrid
                            rows={groupedRows}
                            columns={columns}
                            loading={loading}
                            getRowId={(row) => `${row.location}-${row.materialType}`}
                            height={340}
                            density="compact"
                            noRowsLabel="No data available"
                        />
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
};

export default EmissionsByLocation;
